import clsx from 'clsx';
import * as React from 'react';

import { isDocumentEmpty, trimDocument } from '@gitbook/doc-core';
import type { Change, Value } from '@gitbook/slate';
import { Icon } from '@gitbook/spine-icons';
import { Button } from '@gitbook/spine-react';

import { BasicEditor, type BasicEditorProps, InsertPageMentionButton } from '../BasicEditor';
import { useContentReferenceContext } from '../ContentReferences';

const mentions: BasicEditorProps['mentions'] = {
    '#': ['page'],
    '@': ['page'],
};

const noMentions: BasicEditorProps['mentions'] = {};

/**
 * Input to post a new message in the chat.
 */
export function AgentChatInput(props: {
    value: Value;
    setValue: React.Dispatch<React.SetStateAction<Value>>;

    /**
     * Whether the input is editable.
     */
    editable?: boolean;

    /**
     * Whether the input is submittable.
     */
    submittable?: boolean;

    /**
     * Placeholder for the input.
     */
    placeholder?: string;

    /**
     * Callback to submit the input.
     */
    onSubmit?: () => void;

    /**
     * Class name to apply to the input.
     */
    className?: string;
}) {
    const {
        value,
        editable = true,
        submittable = true,
        setValue,
        className,
        onSubmit,
        placeholder = 'Plan, search, ask, write anything',
    } = props;

    const contentRefContext = useContentReferenceContext();
    const isEmpty = React.useMemo(() => {
        return isDocumentEmpty(trimDocument(value.document));
    }, [value.document]);

    const onChange = (change: Change) => {
        setValue(change.value);
    };

    const onReplaceText = (text: string) => {
        if (editable) {
            setValue(
                (prevValue) =>
                    prevValue.change().selectAll().delete().insertText(text).focus().value
            );
        }
    };

    React.useEffect(() => {
        setValue((value) => value.change().collapseToEndOf(value.document).focus().value);
    }, [setValue]);

    return (
        // biome-ignore lint/a11y/useKeyWithClickEvents: <explanation>
        <div
            className={clsx('flex cursor-text flex-col gap-3', className)}
            onClick={() => onChange(value.change().focus())}
        >
            <BasicEditor
                value={value}
                onChange={onChange}
                readOnly={!editable}
                placeholder={placeholder}
                className="px-4 py-3"
                mentions={contentRefContext.space ? mentions : noMentions}
                onSubmit={submittable ? onSubmit : undefined}
            />
            <div className="flex justify-between gap-3 px-3 pb-2">
                <div className="flex">
                    {contentRefContext.space ? (
                        <>
                            <InsertPageMentionButton readOnly={!editable} setValue={setValue} />
                            <Button
                                size="medium"
                                kind="blank"
                                label="Suggest improvement"
                                leadingIcon={Icon.AIImproveWriting}
                                iconOnly
                                onPress={() => {
                                    onReplaceText('Suggest improvements to page #');
                                }}
                            />
                        </>
                    ) : null}
                </div>
                {onSubmit ? (
                    <div>
                        <Button
                            size="medium"
                            kind="primary"
                            label="Post"
                            disabled={isEmpty || !editable || !submittable}
                            onPress={onSubmit}
                        />
                    </div>
                ) : null}
            </div>
        </div>
    );
}
